import React from "react";
import { TableCell, TableRow, Tooltip } from "@mui/material";
import { formatDisplayDate } from "../../../utils/helperFunctions";
import { QC_STATUS_CHIP } from "../../../utils/constants/index";
import {
  deliveryModerationChipStatus,
  isDeliveryRejected,
  isDeliveryFinalRejected,
} from "../../../utils/deliveryModeration";
import StatusChip from "../../common/StatusChip";
import DirectChip from "../../common/DirectChip";
import { Button } from "../../ui/button";
import { AssetCell, IdPair, NoStage } from "./DeliveryCells";

/** A delivered hook with its request, moderation and QC state. */
const DeliveryRow = ({
  row,
  canCreateDelivery,
  onOpenDelivery,
  onResubmit,
  retryS3,
}) => {
  const chip = deliveryModerationChipStatus(row);
  const qcChip = QC_STATUS_CHIP[row.qc_status] ?? null;
  // qc_status is per row; has_resubmission guesses hook lines, so it only gates the final-rejected case.
  const showResubmit =
    row.qc_status === "changes_requested" ||
    (isDeliveryFinalRejected(row) && !row.has_resubmission);

  return (
    <TableRow
      hover
      onClick={() => onOpenDelivery?.(row.id)}
      sx={{ cursor: onOpenDelivery ? "pointer" : "default" }}
    >
      <TableCell sx={{ color: "text.secondary" }}>
        <IdPair adId={row.ad_id} deliveryId={row.id} />
      </TableCell>
      <TableCell
        sx={{ color: "text.secondary", maxWidth: 220, wordBreak: "break-all" }}
      >
        {row.requested_via === "agency_direct" && <DirectChip />}
        {row.hook_name || row.ad_name || "—"}
      </TableCell>
      <TableCell sx={{ color: "text.secondary" }}>
        <AssetCell delivery={row} retryS3={retryS3} />
      </TableCell>
      <TableCell>
        {row.request_status ? (
          <StatusChip subtle status={row.request_status} size="small" />
        ) : (
          <NoStage />
        )}
      </TableCell>
      <TableCell>
        {row.has_moderation ? (
          <StatusChip subtle status={chip} size="small" />
        ) : (
          <NoStage />
        )}
      </TableCell>
      <TableCell>
        {qcChip ? (
          <Tooltip
            title={row.qc_feedback ? `QC feedback: ${row.qc_feedback}` : ""}
            arrow
          >
            <span>
              <StatusChip subtle status={qcChip} size="small" />
            </span>
          </Tooltip>
        ) : (
          <NoStage />
        )}
      </TableCell>
      <TableCell align="right">
        {showResubmit && canCreateDelivery ? (
          <Button
            type="button"
            size="sm"
            className="h-8 bg-[#7f7ad5] hover:bg-[#6b66c4]"
            onClick={(e) => {
              e.stopPropagation();
              onResubmit(row);
            }}
          >
            Resubmit
          </Button>
        ) : isDeliveryRejected(row) && row.has_resubmission ? (
          <span className="text-xs text-muted-foreground">Newer delivery</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell sx={{ color: "text.secondary" }}>
        {formatDisplayDate(row.created_on) ?? "—"}
      </TableCell>
    </TableRow>
  );
};

export default DeliveryRow;
